<!-- @format -->
<template>
	<el-drawer
		v-model="visabled"
		size="90%"
		:title="`${type === 'add' ? '新增' : type === 'edit' ? '编辑' : ''}${item.resName || resItem.resName}`"
		destroy-on-close
		:close-on-press-escape="false"
		:before-close="beforeClose"
	>
		<div class="drawer-render">
			<header class="drawer-header">
				<div v-if="!disabled">
					<template v-if="type === 'read'">
						<el-button type="primary" @click="type = 'edit'">编辑</el-button>
					</template>
					<template v-else>
						<el-button type="primary" :loading="saveLoading" @click="onSave">保存</el-button>
						<el-button v-if="type !== 'add'" :loading="saveLoading" @click="onCancel">取消</el-button>
					</template>
				</div>
				<!-- else div 用作解决flex布局问题 -->
				<div v-else />

				<div v-if="type !== 'add'" class="drawer-header-right">
					<el-button
						v-if="resourceConfig.relationCompute === '1'"
						type="primary"
						size="small"
						@click="relationVisabled = true"
					>
						关系拓扑
					</el-button>
					<el-button
						v-if="resourceConfig.ifRecordHis === '1'"
						type="primary"
						size="small"
						@click="recordHisState.hisVisabled = true"
					>
						历史记录
					</el-button>
					<pc-qr-code v-if="formData.qrCode" :src="formData.qrCode" class="drawer-header-right-qrCode" />
				</div>
			</header>
			<el-divider />
			<div v-if="!formData.loading" class="drawer-content">
				<template v-if="type !== 'none'">
					<Render
						ref="renderRef"
						v-model:formDatas="formData.data"
						:widget-datas="widgetDatas"
						:disabled="type === 'read'"
						class="drawer-content-render"
						@field-change="fieldChange"
					/>
				</template>
				<div class="drawer-content-anchorList">
					<el-timeline>
						<el-timeline-item
							v-for="(itemVal, index) in timeLineList"
							:key="itemVal.code"
							:type="(!activeAnchor && index === 0) || activeAnchor === itemVal.code ? 'primary' : ''"
							:hide-timestamp="true"
							:hollow="true"
						>
							<el-link
								:href="`#${itemVal.options.code}`"
								:underline="false"
								:type="activeAnchor === itemVal.code ? 'primary' : 'default'"
								@click="onAnchor(itemVal)"
							>
								<span>{{ itemVal.options.label }}</span>
							</el-link>
						</el-timeline-item>
					</el-timeline>
				</div>
			</div>
			<div v-else v-loading="formData.loading" style="height: 100%" />
		</div>

		<el-drawer v-model="recordHisState.hisVisabled" title="历史记录" size="50%">
			<pcp-res-history :objectid="item.objectid || ''" :params="{ bussinessCode }" />
		</el-drawer>

		<el-drawer v-model="relationVisabled" title="关系拓扑" size="90%">
			<iframe
				:src="
					getEnvUrlByUrl(
						`/powereg/model/relation/detail?resCode=${item.resCode}&ids=${item.objectid}&bussinessCode=${bussinessCode}&groupId=2`,
					)
				"
				style="height: 100%; width: 100%"
			/>
		</el-drawer>
	</el-drawer>
</template>

<script setup lang="ts">
	import { getEnvUrlByUrl } from '@power/runtime-vue3';
	import PcpResHistory from './pcp-res-history.vue';
	import { PcQrCode } from '@/components';
	import Render from '@/components/Drag/Render/index.vue';
	import { getResObject } from '@/api/resource';
	import { flatArr } from '@/utils';
	import { getDefaultValue } from '@/components/Drag/utils';

	interface Props {
		type: string; // 当前类型,'add'|'edit'|'read'
		item: any; // 当前选中的列表数据
		widgetDatas: any; // 表单结构
		disabled?: boolean;
		params?: Record<string, any>;
		resourceConfig: {
			relationCompute: '0' | '1';
			ifRecordHis: '0' | '1';
		};
	}

	interface FormData {
		loading: boolean;
		data: Record<string, any>;
		defaultDatas: Record<string, any>;
		qrCode: string;
	}

	interface RessubobjectList {
		resSubcode: string;
		resSubname: string;
		deleteIds: string[];
		data: any[];
	}

	const initFormData = (): FormData => ({
		loading: false,
		data: {},
		defaultDatas: {}, // 保存表单调完接口后的数据，可以直接重置
		qrCode: '',
	});

	const props = withDefaults(defineProps<Props>(), {
		disabled: false,
		params: () => ({}),
		widgetDatas: () => [],
	});

	const emit = defineEmits(['update:type', 'onEdit', 'onAdd']);

	const bussinessCode = computed(() => props.params.bussinessCode);

	const timeLineList = computed(() => props.widgetDatas.filter((itm) => !itm.options.hidden));

	// inject
	// 左侧树点击的节点
	const resItem: any = inject('resItem');

	// 表单数据
	const formData = ref<FormData>(initFormData());

	// drawer是否展开
	const visabled = ref<boolean>(false);

	// 提交按钮是否loading
	const saveLoading = ref<boolean>(false);

	// 渲染器ref
	const renderRef = ref<InstanceType<typeof Render> | null>(null);

	// ressubobject变化的值
	const ressubobjectList = ref<RessubobjectList[]>([]);

	// 选中的锚点
	const activeAnchor = ref<string>('');

	// 历史记录state
	const recordHisState = ref({
		hisVisabled: false,
	});

	const relationVisabled = ref<boolean>(false);

	const type = computed({
		get() {
			return props.type;
		},
		set(value) {
			emit('update:type', value);
			return true;
		},
	});

	watch(type, async (newVal) => {
		visabled.value = type.value !== 'none';
		if (newVal === 'read' && !Object.keys(formData.value.data).length) {
			formData.value.loading = true;
			const res = await getResObject({ objectid: props.item.objectid, ...props.params });

			if (res.code === 200) {
				const data = () => getRessubobjectList(res.data);
				formData.value = { loading: false, data: data(), defaultDatas: data(), qrCode: res.data.qrCode };
			}
		} else if (newVal === 'add') {
			const defaultValue = () => getDefaultValue(props.widgetDatas.filter((itm) => itm.options.Attrtypesign === '1'));
			formData.value = { loading: false, data: defaultValue(), defaultDatas: defaultValue(), qrCode: '' };
		}
	});

	// 后端给过来的数据子项叫ressubobjectList，调整为正常字段格式
	const getRessubobjectList = (data) => {
		const { fieldMap, ressubobjectList = [] } = data;
		const obj = { ...fieldMap };
		(ressubobjectList || []).forEach((item) => {
			obj[item.resSubcode] = item.data;
		});
		return obj;
	};

	// 将obj的value转成字符串
	const valToString = (obj) => {
		const cloneObj = JSON.parse(JSON.stringify(obj));
		for (const key in cloneObj) {
			cloneObj[key] = obj[key].toString();
		}
		return cloneObj;
	};

	// 往数组中插入一个数据，如果存在同样的id则替换
	const putArr = (arr, item, code?: string) => {
		const idx = arr.findIndex((itm) => (code ? itm[code] === item[code] : itm === item));
		if (idx === -1) {
			arr.push(item);
		} else {
			arr.splice(idx, 1, item);
		}
	};

	const fieldChange = (type: 'add' | 'edit' | 'del', item, parent) => {
		// 数据需要转成字符串
		const data = valToString(item);

		const idx = ressubobjectList.value.findIndex((itm) => itm.resSubcode === parent.options.code);

		if (idx !== -1) {
			if (type === 'del') {
				putArr(ressubobjectList.value[idx].deleteIds, data.objectid);
				const index = ressubobjectList.value[idx].data.findIndex((itm) => itm.objectid === data.objectid);
				if (index !== -1) {
					ressubobjectList.value[idx].data.splice(index, 1);
				}
			} else {
				putArr(ressubobjectList.value[idx].data, data, 'objectid');
			}
		} else {
			if (type === 'del') {
				ressubobjectList.value.push({
					resSubcode: parent.options.code,
					resSubname: parent.options.label,
					deleteIds: [data.objectid],
					data: [],
				});
			} else {
				ressubobjectList.value.push({
					resSubcode: parent.options.code,
					resSubname: parent.options.label,
					deleteIds: [],
					data: [data],
				});
			}
		}
	};

	// 列表字段放置ressubobjectList中，组装ressubobjectList数据
	const delRessubobjectList = (renderDatas) => {
		const flatWigetDatas = flatArr(props.widgetDatas);

		flatWigetDatas.forEach((itm) => {
			// 列表字段放置ressubobjectList中
			if (itm.options.Attrtypesign === '2') {
				// 主项不需要列表字段
				delete renderDatas[itm.options.code];
			}
		});

		return renderDatas;
	};

	const onSave = () => {
		const formRef = renderRef.value?.formRef;

		formRef!.validate(async (vaild) => {
			if (vaild) {
				saveLoading.value = true;

				// 数据需要转成字符串
				const fieldMap = delRessubobjectList(valToString(formData.value.data));
				const params = {
					fieldMap,
					ressubobjectList: ressubobjectList.value,
				};
				if (type.value === 'edit') {
					emit(
						'onEdit',
						{
							...params,
							resCode: props.item.resCode,
							resName: props.item.resName,
							objectid: props.item.objectid,
						},
						() => {
							onClose();
							ElMessage({
								message: '更新成功',
								type: 'success',
							});
						},
						() => {
							saveLoading.value = false;
						},
					);
				} else {
					emit(
						'onAdd',
						{
							...params,
							resCode: resItem.value.resCode,
							resName: resItem.value.resName,
						},
						() => {
							onClose();
							ElMessage({
								message: '保存成功',
								type: 'success',
							});
						},
						() => {
							saveLoading.value = false;
						},
					);
				}
			}
		});
	};

	const onCancel = () => {
		type.value = 'read';
		const data = JSON.parse(JSON.stringify(formData.value.defaultDatas));
		formData.value.data = data;
	};

	const beforeClose = (done) => {
		const isDiff = JSON.stringify(formData.value.data) === JSON.stringify(formData.value.defaultDatas);
		const close = () => {
			done();
			onClose();
		};

		isDiff || type.value === 'read'
			? close()
			: ElMessageBox.confirm('请确认是否有数据没保存,点击确定退出', 'warnning', {
					confirmButtonText: '确定',
					cancelButtonText: '取消',
					type: 'warning',
					center: true,
			  })
					.then(() => {
						close();
					})
					.catch(() => {});
	};

	const onClose = () => {
		saveLoading.value = false;
		type.value = 'none';
		formData.value = initFormData();
		ressubobjectList.value = [];
		activeAnchor.value = '';
	};

	const onAnchor = (item) => {
		activeAnchor.value = item.code;
	};
</script>

<style scoped lang="scss">
	.drawer-render {
		height: 100%;

		.drawer-header {
			display: flex;
			justify-content: space-between;
			align-items: center;

			.drawer-header-right {
				display: flex;
				align-items: center;

				.drawer-header-right-qrCode {
					margin-left: 10px;
				}
			}
		}

		.el-divider--horizontal {
			margin: 5px 0 !important;
		}

		.drawer-content {
			display: flex;
			height: calc(100% - 40px);
			gap: 10px;
			padding-bottom: 5px;

			.drawer-content-render {
				height: 100%;
				overflow-y: scroll;
				flex: 1;
				padding-right: 5px;
			}

			.drawer-content-anchorList {
				height: 100%;
				width: 100px;
				margin-top: 5px;

				.el-timeline {
					padding: 0px;
				}
			}
		}
	}
</style>
